/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { Icon } from '@/components/Icon';
import { useDismissBootSplash } from '@/components/loading/bootSplash';
import { clearMsalCache, loginRequest } from '@/auth/msalConfig';
import { clearExpired, clearForbidden } from '@/auth/accessState';

import {
  STATUS_ACTION,
  STATUS_ACTIONS,
  STATUS_BODY,
  STATUS_DISC,
  STATUS_HOME_LINK,
  STATUS_PAGE_BARE,
  STATUS_TITLE,
} from './statusPage';

/** Access page for expired sessions (401) and authenticated-but-denied users (403). */
export default function UnauthorizedPage() {
  const { t } = useTranslation();
  const { signIn, signOut, isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  // Deep-linking here on a dead session skips the shell, so nothing else clears the boot splash.
  useDismissBootSplash();

  const reason = params.get('reason');
  const noAccess = reason === 'forbidden' || (reason == null && isAuthenticated);

  if (noAccess) {
    return (
      <div className={STATUS_PAGE_BARE}>
        <span className={STATUS_DISC}>
          <Icon name="warn" size={30} />
        </span>
        <h1 className={STATUS_TITLE}>{t('unauthorized.title')}</h1>
        <p className={STATUS_BODY}>{t('unauthorized.deniedMessage')}</p>
        <div className={STATUS_ACTIONS}>
          {isAuthenticated ? (
            <button
              type="button"
              onClick={() => {
                // Clear access verdicts before sign-out so the guard cannot pin /unauthorized.
                clearForbidden();
                clearExpired();
                signOut();
              }}
              className={STATUS_ACTION}
            >
              {t('unauthorized.signOut')}
            </button>
          ) : null}
          <Link to="/" className={STATUS_HOME_LINK}>
            {t('unauthorized.home')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={STATUS_PAGE_BARE}>
      <span className={STATUS_DISC}>
        <Icon name="warn" size={30} />
      </span>
      <h1 className={STATUS_TITLE}>{t('unauthorized.sessionTitle')}</h1>
      <p className={STATUS_BODY}>{t('unauthorized.sessionMessage')}</p>
      <div className={STATUS_ACTIONS}>
        <button
          type="button"
          onClick={async () => {
            // Drop the expired verdict so the post-redirect session isn't immediately re-gated.
            clearExpired();
            // Purge any stale/stuck MSAL cache first so re-auth starts clean (incognito-like), but
            // never let a cache-clear failure block sign-in - the redirect must always fire.
            try {
              await clearMsalCache();
            } finally {
              signIn({ ...loginRequest, redirectStartPage: window.location.origin });
            }
          }}
          className={STATUS_ACTION}
        >
          {t('unauthorized.signIn')}
        </button>
      </div>
    </div>
  );
}
